/**
 * subscription-widget-appstle.js
 * Card-based subscription widget for Horizon theme + Appstle
 *
 * Key features:
 * - Intercepts fetch/XHR to /cart/add to guarantee selling_plan reaches Shopify
 *   (Appstle's minified script can strip it if its own widget has no selection)
 * - Syncs selection to Appstle's native <select> when it appears in the DOM
 * - Replaces widget HTML on variant change so prices/plans update correctly
 */
(function () {
  'use strict';

  const VARIANT_UPDATE_EVENT = 'variant:update';

  function isSwDebugEnabled() {
    try {
      return window.location.search.indexOf('swDebug=1') > -1 || window.localStorage.getItem('swDebug') === '1';
    } catch (e) {
      return false;
    }
  }

  function swLog(scope, payload) {
    if (!isSwDebugEnabled()) return;
    if (payload !== undefined) {
      console.log('[SW DEBUG][' + scope + ']', payload);
      return;
    }
    console.log('[SW DEBUG][' + scope + ']');
  }

  function getWidgetSellingPlan() {
    const widget = document.querySelector('[id^="custom-sub-widget-"]');
    if (!widget) return undefined;
    const checked = widget.querySelector('.sub-widget__radio:checked');
    if (!checked) return undefined;
    if (checked.value === 'one-time') return '';
    var plan = checked.getAttribute('data-plan-id')
      || checked.value.replace('subscribe-', '')
      || '';
    swLog('getWidgetSellingPlan', {
      widgetId: widget.id,
      checkedValue: checked.value,
      planId: plan,
    });
    return plan;
  }

  function patchBody(body) {
    const planId = getWidgetSellingPlan();
    swLog('patchBody:start', {
      planId: planId,
      bodyType: body && body.constructor ? body.constructor.name : typeof body,
    });
    if (planId === undefined) return body;

    if (body instanceof FormData) {
      if (planId) body.set('selling_plan', planId);
      else body.delete('selling_plan');
      return body;
    }

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        if (Array.isArray(json.items)) {
          json.items.forEach(function (item) {
            if (planId) item.selling_plan = parseInt(planId, 10);
            else delete item.selling_plan;
          });
        } else if (json.id !== undefined) {
          if (planId) json.selling_plan = parseInt(planId, 10);
          else delete json.selling_plan;
        }
        return JSON.stringify(json);
      } catch (e) { /* not JSON, return as-is */ }
    }

    return body;
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && /\/cart\/add/.test(url);
  }

  function installFetchInterceptor() {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
        if (isCartAddUrl(url) && init && init.body) {
          init = Object.assign({}, init);
          init.body = patchBody(init.body);
        }
      } catch (e) { /* never break fetch */ }
      return origFetch.apply(this, arguments);
    };
  }

  function installXHRInterceptor() {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__swUrl = url;
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (isCartAddUrl(this.__swUrl) && body) {
          arguments[0] = patchBody(body);
        }
      } catch (e) { /* never break XHR */ }
      return origSend.apply(this, arguments);
    };
  }

  function reinstallFetchInterceptor() {
    setTimeout(function () {
      installFetchInterceptor();
    }, 2500);
  }

  function syncAppstleSelects(planId) {
    var selects = document.querySelectorAll('[id^="appstle_selling_plan"]');
    swLog('syncAppstleSelects', {
      planId: planId || '',
      selectCount: selects.length,
    });
    for (var i = 0; i < selects.length; i++) {
      var sel = selects[i];
      if (sel.tagName === 'SELECT') {
        sel.value = planId || '';
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function watchForAppstleSelects(initialPlanId) {
    var found = false;
    var observer = new MutationObserver(function () {
      var sel = document.querySelector('[id^="appstle_selling_plan"]');
      if (sel && sel.tagName === 'SELECT' && !found) {
        found = true;
        observer.disconnect();
        if (initialPlanId) syncAppstleSelects(initialPlanId);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
      var sel = document.querySelector('[id^="appstle_selling_plan"]');
      if (sel && sel.tagName === 'SELECT' && initialPlanId) {
        syncAppstleSelects(initialPlanId);
      }
    }, 5000);
  }

  /* ====================================================================
   *  Widget class
   * ==================================================================== */

  class AppstleCustomWidget {
    constructor(productId, sectionId, widgetIndex) {
      this.productId = productId;
      this.sectionId = sectionId;
      this.widgetIndex = widgetIndex || 0;

      this.widget = document.getElementById('custom-sub-widget-' + productId);
      if (!this.widget) return;

      this.radios        = this.widget.querySelectorAll('.sub-widget__radio');
      this.cards         = this.widget.querySelectorAll('.sub-widget__card');
      this.subscribeHeader   = this.widget.querySelector('[data-subscribe-header]');
      this.subscribeCards    = this.widget.querySelector('[data-subscribe-cards]');
      this.subscribeCheckbox = this.widget.querySelector('[data-subscribe-checkbox]');

      this._bindEvents();
      this._applyInitialSelection();
    }

    /* ------------------------------------------------------------------
     *  Events
     * ------------------------------------------------------------------ */

    _bindEvents() {
      var self = this;

      // Disable add to cart on init — nothing selected yet
      self._setAddToCartState(false);

      if (this.radios) {
        this.radios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            self._onSelectionChange();
          });
        });
      }

      if (this.subscribeHeader) {
        this.subscribeHeader.addEventListener('click', function () {
          self._onSubscribeHeaderClick();
        });
      }
    }

    /* ------------------------------------------------------------------
     *  Subscribe header click
     *  - If one-time is currently selected → switch to subscribe mode
     *    (expand cards, uncheck one-time, but don't auto-select a plan)
     *  - If subscribe mode is already expanded → collapse to one-time
     * ------------------------------------------------------------------ */

    _onSubscribeHeaderClick() {
      var selected   = this._getSelectedRadio();
      var isOneTime  = selected && selected.value === 'one-time';
      var section    = this.widget.querySelector('[data-subscribe-section]');
      var isCollapsed = section && section.classList.contains('sub-widget__subscribe-section--collapsed');

      if (isOneTime || isCollapsed) {
        // Switch to subscribe mode: uncheck one-time, show cards, but select nothing

        if (selected && selected.value === 'one-time') {
          selected.checked = false;
        }
        // Remove active from one-time card
        var oneTimeCard = this.widget.querySelector('.sub-widget__card--onetime');
        if (oneTimeCard) oneTimeCard.classList.remove('sub-widget__card--active');

        // Show subscribe section
        if (section) section.classList.remove('sub-widget__subscribe-section--collapsed');
        if (this.subscribeCards) this.subscribeCards.hidden = false;
        if (this.subscribeHeader) this.subscribeHeader.setAttribute('aria-expanded', 'true');
        if (this.subscribeCheckbox) this.subscribeCheckbox.classList.add('sub-widget__subscribe-checkbox--checked');

        // Nothing selected → disable Add to Cart
        this._setAddToCartState(false);

        // Show first plan price in header (no radio selected yet)
        this._triggerPriceWidgetUpdate();

      } else {
        // Collapse subscribe → select one-time
        var oneTimeRadio = this.widget.querySelector('.sub-widget__radio[value="one-time"]');
        if (oneTimeRadio) {
          oneTimeRadio.checked = true;
          this._onSelectionChange();
        }
      }
    }

    /* ------------------------------------------------------------------
     *  Selection change — single source of truth
     * ------------------------------------------------------------------ */

    _onSelectionChange() {
      var selected = this._getSelectedRadio();
      // Enable add to cart only if a radio is selected
      this._setAddToCartState(!!selected);

      swLog('selectionChange:start', {
        widgetId: this.widget && this.widget.id,
        productId: this.productId,
        selectedValue: selected ? selected.value : null,
      });

      if (!selected) {
        this._updateSubscribeHeaderActiveClass();
        return;
      }

      var isOneTime = selected.value === 'one-time';
      var planId = !isOneTime
        ? (selected.getAttribute('data-plan-id') || selected.value.replace('subscribe-', ''))
        : '';

      // Update active class on all cards
      if (this.cards) {
        this.cards.forEach(function (card) {
          var cardRadio = card.querySelector('.sub-widget__radio');
          card.classList.toggle('sub-widget__card--active', cardRadio === selected);
        });
      }
      this._updateSubscribeHeaderActiveClass();
      // Update subscribe section visibility
      var section = this.widget.querySelector('[data-subscribe-section]');

      if (isOneTime) {
        // One-time selected → collapse subscribe section
        if (this.subscribeCards) this.subscribeCards.hidden = true;
        if (this.subscribeHeader) this.subscribeHeader.setAttribute('aria-expanded', 'false');
        if (this.subscribeCheckbox) this.subscribeCheckbox.classList.remove('sub-widget__subscribe-checkbox--checked');
        if (section) section.classList.add('sub-widget__subscribe-section--collapsed');
        this._clearSellingPlan();
      } else {
        // Subscribe plan selected → expand subscribe section
        if (this.subscribeCards) this.subscribeCards.hidden = false;
        if (this.subscribeHeader) this.subscribeHeader.setAttribute('aria-expanded', 'true');
        if (this.subscribeCheckbox) this.subscribeCheckbox.classList.add('sub-widget__subscribe-checkbox--checked');
        if (section) section.classList.remove('sub-widget__subscribe-section--collapsed');
        if (planId) this._setSellingPlan(planId);
      }

      // Always trigger price widget update after selection change
      this._triggerPriceWidgetUpdate();

      swLog('selectionChange:end', {
        widgetId: this.widget && this.widget.id,
        selectedValue: selected ? selected.value : null,
        sectionCollapsed: !!(section && section.classList.contains('sub-widget__subscribe-section--collapsed')),
      });
    }
 // Add or remove 'sub-widget-value-active' on subscribe header based on card active state
    _updateSubscribeHeaderActiveClass() {
      if (!this.subscribeHeader) return;
      var anyActiveSubscribe = false;
      if (this.cards) {
        this.cards.forEach(function (card) {
          if (
            card.classList.contains('sub-widget__card--subscribe') &&
            card.classList.contains('sub-widget__card--active')
          ) {
            anyActiveSubscribe = true;
          }
        });
      }
      if (anyActiveSubscribe) {
        this.subscribeHeader.classList.add('sub-widget-value-active');
      } else {
        this.subscribeHeader.classList.remove('sub-widget-value-active');
      }
    }
    /* ------------------------------------------------------------------
     *  Trigger price widget to re-evaluate
     * ------------------------------------------------------------------ */

    _triggerPriceWidgetUpdate() {
      if (typeof initSubscriptionPriceWidget === 'function') {
        initSubscriptionPriceWidget(this.widget);
      }
    }

    /* ------------------------------------------------------------------
     *  Initial state — nothing pre-selected
     * ------------------------------------------------------------------ */

    _applyInitialSelection() {
       if (this.radios) {
        this.radios.forEach(function (r) { r.checked = false; });
      }
      // Remove active from all cards
      if (this.cards) {
        this.cards.forEach(function (c) { c.classList.remove('sub-widget__card--active'); });
      
      }
      // Show subscribe section expanded by default, checkbox checked
      var section = this.widget.querySelector('[data-subscribe-section]');
      if (this.subscribeCards) this.subscribeCards.hidden = false;
      if (this.subscribeHeader) this.subscribeHeader.setAttribute('aria-expanded', 'true');
      if (this.subscribeCheckbox) this.subscribeCheckbox.classList.add('sub-widget__subscribe-checkbox--checked');
      if (section) section.classList.remove('sub-widget__subscribe-section--collapsed');
      this._clearSellingPlan();

      // Nothing selected on init → disable Add to Cart
      this._setAddToCartState(false);

      // Show first plan price in header on initial load
      this._triggerPriceWidgetUpdate();
    }

    /* ------------------------------------------------------------------
     *  Helpers
     * ------------------------------------------------------------------ */

    _getSelectedRadio() {
      return this.widget && this.widget.querySelector('.sub-widget__radio:checked');
    }

    _setAddToCartState(enabled) {
      var btn = document.querySelector('[data-testid="standalone-add-to-cart"]');
      if (!btn) return;
      btn.disabled = !enabled;
      btn.style.opacity = enabled ? '1' : '0.4';
      btn.style.cursor = enabled ? 'pointer' : 'not-allowed';
      btn.style.pointerEvents = enabled ? 'auto' : 'auto';
    }

    _getSellingPlanInput() {
      return document.getElementById('selling-plan-input-' + this.sectionId);
    }

    _setSellingPlan(planId) {
      var input = this._getSellingPlanInput();
      if (input) {
        input.setAttribute('name', 'selling_plan');
        input.value = planId;
      }
      syncAppstleSelects(planId);
    }

    _clearSellingPlan() {
      var input = this._getSellingPlanInput();
      if (input) {
        input.removeAttribute('name');
        input.value = '';
      }
      syncAppstleSelects('');
    }

    destroy() {
      this.widget = null;
      this.radios = null;
      this.cards  = null;
    }
  }

  /* ====================================================================
   *  Widget lifecycle
   * ==================================================================== */

  var widgetInstances = new Map();

  function initWidgets() {
    var widgets = document.querySelectorAll('[id^="custom-sub-widget-"]');
    swLog('initWidgets', { widgetCount: widgets.length });
    widgets.forEach(function (el) {
      var productId = el.getAttribute('data-product-id');
      var sectionId = el.getAttribute('data-section-id');
      if (productId && sectionId && !widgetInstances.has(el.id)) {
        var instance = new AppstleCustomWidget(productId, sectionId, 0);
        if (instance.widget) {
          widgetInstances.set(el.id, instance);
        }
      }
    });
  }

  function destroyWidgets() {
    widgetInstances.forEach(function (inst) { inst.destroy(); });
    widgetInstances.clear();
  }

  function replaceWidgetWithNewHtml(htmlDoc, targetProductId) {
    if (!htmlDoc || typeof htmlDoc.querySelector !== 'function') return;
    var newWidget = null;

    if (targetProductId) {
      newWidget = htmlDoc.querySelector('[id^="custom-sub-widget-"][data-product-id="' + targetProductId + '"]');
    }

    if (!newWidget) {
      newWidget = htmlDoc.querySelector('[id^="custom-sub-widget-"]');
    }

    var currentWidget = null;

    if (newWidget && newWidget.id) {
      currentWidget = document.getElementById(newWidget.id);
    }

    if (!currentWidget && newWidget) {
      var productId = newWidget.getAttribute('data-product-id');
      if (productId) {
        currentWidget = document.querySelector('[id^="custom-sub-widget-"][data-product-id="' + productId + '"]');
      }
    }

    if (!currentWidget) {
      currentWidget = document.querySelector('[id^="custom-sub-widget-"]');
    }

    swLog('replaceWidget:resolved', {
      hasNewWidget: !!newWidget,
      newWidgetId: newWidget ? newWidget.id : null,
      newProductId: newWidget ? newWidget.getAttribute('data-product-id') : null,
      currentWidgetId: currentWidget ? currentWidget.id : null,
      currentProductId: currentWidget ? currentWidget.getAttribute('data-product-id') : null,
    });

    if (!currentWidget) return;

    destroyWidgets();

    if (newWidget) {
      var imported = document.importNode(newWidget, true);
      currentWidget.parentNode.replaceChild(imported, currentWidget);
    } else {
      currentWidget.style.display = 'none';
      var sectionId = currentWidget.getAttribute('data-section-id');
      var input = sectionId ? document.getElementById('selling-plan-input-' + sectionId) : null;
      if (input) {
        input.removeAttribute('name');
        input.value = '';
      }
    }
    setTimeout(initWidgets, 0);
  }

  /* ====================================================================
   *  Bootstrap
   * ==================================================================== */

  function init() {
    installFetchInterceptor();
    installXHRInterceptor();
    reinstallFetchInterceptor();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { setTimeout(initWidgets, 300); });
    } else {
      setTimeout(initWidgets, 300);
    }

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () { destroyWidgets(); initWidgets(); }, 100);
    });

    document.addEventListener(VARIANT_UPDATE_EVENT, function (event) {
      var html = event.detail && event.detail.data && event.detail.data.html;
      var targetProductId = event.detail && event.detail.data && event.detail.data.productId;
      swLog('variant:update:event', {
        sourceId: event.detail && event.detail.sourceId,
        productId: targetProductId,
        variantId: event.detail && event.detail.resource && event.detail.resource.id,
        hasHtml: !!html,
      });
      if (html) {
        replaceWidgetWithNewHtml(html, targetProductId);
      } else {
        destroyWidgets();
        setTimeout(initWidgets, 150);
      }
    });
  }

  init();
})();

/* ------------------------------------------------------------------
 *  Global debug logger (used by code outside IIFE)
 * ------------------------------------------------------------------ */
if (typeof window.__swLog !== 'function') {
  window.__swLog = function (scope, payload) {
    var enabled = false;
    try {
      enabled = window.location.search.indexOf('swDebug=1') > -1 || window.localStorage.getItem('swDebug') === '1';
    } catch (e) {
      enabled = false;
    }
    if (!enabled) return;
    if (payload !== undefined) {
      console.log('[SW DEBUG][' + scope + ']', payload);
    } else {
      console.log('[SW DEBUG][' + scope + ']');
    }
  };
}

function swLog(scope, payload) {
  window.__swLog(scope, payload);
}

/* ====================================================================
 *  Subscription Price Widget
 *  Logic:
 *  - Autoship expanded + NO radio selected → show FIRST subscription plan price
 *  - Autoship expanded + plan selected     → show SELECTED plan price
 *  - One Time selected / Autoship collapsed → hide price from header
 * ==================================================================== */

function initSubscriptionPriceWidget(widgetElement) {
  const widget = widgetElement || document.querySelector('.sub-widget');
  if (!widget) return;

  const headerPriceMode = widget.getAttribute('data-header-price-mode') || 'default';
  const showHeaderOnSelectOnly = headerPriceMode === 'on-select';

  const priceContainer = widget.querySelector(".sub_widget_title_price_container");
  const priceBox       = widget.querySelector(".sub-widget__card_active_pricing");
  const priceComBox    = widget.querySelector(".sub-widget__card_active_pricing_compare");
  const saveBadge      = widget.querySelector(".sub_widget_title_price_container .sub-widget__card-badge");
  const radios         = widget.querySelectorAll('input[name^="custom-purchase-type"]');

  /* ------------------------------------------------------------------
   *  Core: figure out which radio's data to display
   *  Priority:
   *    1. A subscription radio that is currently checked
   *    2. Autoship section is expanded but nothing checked → first sub radio
   *    3. One-time selected or section collapsed → hide
   * ------------------------------------------------------------------ */
  function getDisplayRadio() {
    const subscribeHeader = widget.querySelector('.sub-widget__subscribe-header');
    const isExpanded = subscribeHeader
      ? subscribeHeader.getAttribute('aria-expanded') === 'true'
      : false;

    if (!isExpanded) return null; // collapsed → hide

    // Is a subscription radio checked?
    const checkedRadio = widget.querySelector('input[name^="custom-purchase-type"]:checked');
    if (checkedRadio && checkedRadio.value !== 'one-time') {
      return checkedRadio; // selected plan
    }

    if (checkedRadio && checkedRadio.value === 'one-time') {
      return null; // one-time selected → hide
    }

    // For products configured as on-select, keep header pricing hidden
    // until user explicitly selects a subscription plan.
    if (showHeaderOnSelectOnly) {
      return null;
    }

    // Nothing selected but autoship is expanded → use first subscription radio
    const firstSubRadio = widget.querySelector(
      '.sub-widget__card--subscribe .sub-widget__radio'
    );
    return firstSubRadio || null;
  }

  /* ------------------------------------------------------------------
   *  Render price in header based on resolved radio
   * ------------------------------------------------------------------ */
  function updateActivePrice() {
    const radioToUse = getDisplayRadio();

    const checkedRadio = widget.querySelector('input[name^="custom-purchase-type"]:checked');
    const header = widget.querySelector('.sub-widget__subscribe-header');
    const isExpanded = header ? header.getAttribute('aria-expanded') === 'true' : false;
    swLog('priceWidget:updateActivePrice', {
      widgetId: widget.id || null,
      productId: widget.getAttribute('data-product-id'),
      checkedValue: checkedRadio ? checkedRadio.value : null,
      isExpanded: isExpanded,
      displayRadioValue: radioToUse ? radioToUse.value : null,
      displayPrice: radioToUse ? Number(radioToUse.dataset.price) : null,
      displayCompare: radioToUse ? Number(radioToUse.dataset.compare) : null,
      displaySave: radioToUse ? Number(radioToUse.dataset.savePercent) : null,
    });

    if (!radioToUse) {
      if (header) header.classList.remove('sub-widget-value-active');
      // Hide everything
      if (priceContainer) priceContainer.style.display = 'none';
      if (priceComBox)    priceComBox.style.display    = 'none';
      if (saveBadge)      saveBadge.style.display      = 'none';
      return;
    }

    // Keep CSS visibility state aligned with computed display data.
    if (header) header.classList.add('sub-widget-value-active');

    const price   = Number(radioToUse.dataset.price);
    const compare = Number(radioToUse.dataset.compare);
    const save    = Number(radioToUse.dataset.savePercent);

    // Show price container
    if (priceContainer && priceBox) {
      if (price) {
        priceContainer.style.display = 'block';
        priceBox.innerHTML = formatMoney(price);
      } else {
        priceContainer.style.display = 'none';
      }
    }

    // Compare price
    if (priceComBox) {
      if (compare && compare > price) {
        priceComBox.innerHTML  = formatMoney(compare);
        priceComBox.style.display = 'block';
      } else {
        priceComBox.style.display = 'none';
      }
    }

    // Save badge
    if (saveBadge) {
      if (save && save > 0) {
        saveBadge.innerHTML      = 'Save ' + Math.round(save) + '%';
        saveBadge.style.display  = 'inline-block';
      } else {
        saveBadge.style.display  = 'none';
      }
    }
  }

  function formatMoney(cents) {
    if (!cents) return '';
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  /* ------------------------------------------------------------------
   *  Bind change events (clean re-bind safe for variant updates)
   * ------------------------------------------------------------------ */
  radios.forEach(function (radio) {
    if (radio._priceHandler) {
      radio.removeEventListener('change', radio._priceHandler);
    }
    radio._priceHandler = function () {
      updateActivePrice();
    };
    radio.addEventListener('change', radio._priceHandler);
  });

  /* ------------------------------------------------------------------
   *  Run immediately on init
   * ------------------------------------------------------------------ */
  updateActivePrice();
}

/* ================= INIT ================= */

document.addEventListener('DOMContentLoaded', function () {
  const widgets = document.querySelectorAll('.sub-widget');
  widgets.forEach(function (widget) {
    initSubscriptionPriceWidget(widget);
  });
});

/* ================= VARIANT CHANGE FIX ================= */

document.addEventListener('variant:update', function () {
  setTimeout(function () {
    const widgets = document.querySelectorAll('.sub-widget');
    widgets.forEach(function (widget) {
      initSubscriptionPriceWidget(widget);
    });
  }, 400);
});

/* ================= HOMEPAGE REINIT SAFETY ================= */

function reinitAllSubscriptionWidgets() {
  const widgets = document.querySelectorAll('.sub-widget');
  widgets.forEach(function (widget) {
    initSubscriptionPriceWidget(widget);
  });
}

(function installSubscriptionWidgetReinitSafety() {
  var debounceTimer;

  function scheduleReinit(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      reinitAllSubscriptionWidgets();
    }, delay || 120);
  }

  // Safety for homepage/custom variant flows that do DOM replacements
  if (typeof MutationObserver === 'function' && document.body) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (!m.addedNodes || !m.addedNodes.length) continue;

        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (!(node instanceof Element)) continue;

          if (
            (node.matches && (node.matches('.sub-widget') || node.matches('[id^="custom-sub-widget-"]'))) ||
            (node.querySelector && node.querySelector('.sub-widget, [id^="custom-sub-widget-"]'))
          ) {
            scheduleReinit(80);
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Extra safety: variant tile clicks on homepage
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var tile = target.closest('.product-replacement-filters .card, .product-replacement-filters [data-option-value-id], .product-replacement-filters label');
    if (tile) {
      scheduleReinit(220);
    }
  });
})();
